/**
 * TeamChatPage — Canais internos DSC Travel
 * v3: slash menu /visto /passaporte + cards de processo
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Plus, X, Users } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useTeamUnread } from '../../hooks/useTeamUnread';
import TeamChatApi from '../services/teamChatApi';
import {
  subscribeToChannelMessages,
} from '../services/teamChatRealtime';
import './TeamChat.css';

const API = process.env.REACT_APP_CRM_API_URL || 'http://localhost:5000/api/crm';

function playNotification() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = 'sine';
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.12);
    g.gain.setValueAtTime(0.2, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
    o.start(ctx.currentTime); o.stop(ctx.currentTime + 0.25);
  } catch (_) {}
}

function formatTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function formatDateLabel(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
  const sameDay = (a, b) => a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  if (sameDay(d, today)) return 'Hoje';
  if (sameDay(d, yesterday)) return 'Ontem';
  return d.toLocaleDateString('pt-BR');
}

function initials(name = '') {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

// ── Thread ────────────────────────────────────────────────────
function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 60000);
  if (diff < 1) return 'agora';
  if (diff < 60) return `há ${diff} minuto${diff > 1 ? 's' : ''}`;
  const h = Math.floor(diff / 60);
  if (h < 24) return `há ${h} hora${h > 1 ? 's' : ''}`;
  return formatTime(dateStr);
}

function ThreadMsg({ author, time, text, big }) {
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#dfe5e7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: '#54656f', flexShrink: 0 }}>
        {initials(author)}
      </div>
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1d1c1d' }}>{author}</span>
          <span style={{ fontSize: 12, color: '#616061' }}>{timeAgo(time)}</span>
        </div>
        <p style={{ fontSize: 14, color: '#1d1c1d', margin: 0, lineHeight: 1.5 }}>{text}</p>
      </div>
    </div>
  );
}

function ThreadPanel({ message, sellerId, onClose, onThreadSent }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);
  const endRef = useRef(null);
  const threadCount = (message.threads || []).length;

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [message.threads]);
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 100); }, []);

  const handleSend = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await TeamChatApi.sendThread(message.id, sellerId, t);
      setText('');
      playNotification();
      if (onThreadSent) onThreadSent(message.id);
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  return (
    <div className="tc-thread-panel">
      <div className="tc-thread-header">
        <h4>Conversa</h4>
        <button className="tc-thread-close" onClick={onClose}>×</button>
      </div>
      <div className="tc-thread-messages" style={{ background: '#fff', padding: '20px 16px' }}>
        <ThreadMsg author={message.sellers?.name || 'Equipe'} time={message.created_at} text={message.text} big />
        {threadCount > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 20px' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1264a3', whiteSpace: 'nowrap' }}>{threadCount} resposta{threadCount > 1 ? 's' : ''}</span>
            <div style={{ flex: 1, height: 1, background: '#e8e8e8' }} />
          </div>
        )}
        {(message.threads || []).map(t => (
          <ThreadMsg key={t.id} author={t.sellers?.name || 'Equipe'} time={t.created_at} text={t.text} />
        ))}
        <div ref={endRef} />
      </div>
      <div style={{ padding: '12px 16px', borderTop: '1px solid #e8e8e8', background: '#fff' }}>
        <div style={{ border: '1px solid #c4c4c4', borderRadius: 8, overflow: 'hidden' }}>
          <input
            ref={inputRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            placeholder="Responder..."
            style={{ width: '100%', fontSize: 14, padding: '10px 12px', border: 'none', outline: 'none', color: '#1d1c1d', background: '#fff', boxSizing: 'border-box' }}
          />
          <div style={{ padding: '8px 12px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e8e8e8' }}>
            <button onClick={handleSend} disabled={!text.trim() || sending}
              style={{ background: text.trim() ? '#007a5a' : '#ccc', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 12px', cursor: text.trim() ? 'pointer' : 'default', fontSize: 13 }}>
              ›
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MentionDropdown ───────────────────────────────────────────
function MentionDropdown({ members, query, onSelect }) {
  const filtered = members.filter(m => m.name.toLowerCase().startsWith(query.toLowerCase()));
  if (!filtered.length) return null;
  return (
    <div style={{ position: 'absolute', bottom: 52, left: 16, right: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', zIndex: 10, overflow: 'hidden' }}>
      {filtered.map(m => (
        <div key={m.id} onMouseDown={e => { e.preventDefault(); onSelect(m); }}
          style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: '#111b21', display: 'flex', alignItems: 'center', gap: 8 }}
          onMouseEnter={e => e.currentTarget.style.background = '#f0f2f5'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#dfe5e7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 600, color: '#54656f' }}>
            {initials(m.name)}
          </div>
          {m.name}
        </div>
      ))}
    </div>
  );
}

// ── SlashMenu ─────────────────────────────────────────────────
function SlashMenu({ onSelect }) {
  const options = [
    { type: 'visto',      label: '🛂 /visto',      desc: 'Iniciar processo de visto' },
    { type: 'passaporte', label: '📘 /passaporte', desc: 'Iniciar processo de passaporte' },
  ];
  return (
    <div style={{ position: 'absolute', bottom: 52, left: 16, right: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 20, overflow: 'hidden' }}>
      <div style={{ padding: '6px 12px', fontSize: 11, color: '#8696a0', fontWeight: 600, borderBottom: '1px solid #f0f2f5', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Processos
      </div>
      {options.map(o => (
        <div key={o.type} onMouseDown={e => { e.preventDefault(); onSelect(o.type); }}
          style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
          onMouseEnter={e => e.currentTarget.style.background = '#f0f2f5'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#111b21', minWidth: 110 }}>{o.label}</span>
          <span style={{ fontSize: 12, color: '#8696a0' }}>{o.desc}</span>
        </div>
      ))}
    </div>
  );
}

// ── ProcessForm ───────────────────────────────────────────────
const CRM_API = process.env.REACT_APP_CRM_API_URL || 'http://localhost:5000/api/crm';

function ProcessForm({ type, onConfirm, onCancel }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null); // { id, display_name, phone }
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 50); }, []);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setSelected(null);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${CRM_API}/contacts?search=${encodeURIComponent(val)}&limit=8`);
        const data = await res.json();
        setResults(data);
      } catch (_) {}
      finally { setSearching(false); }
    }, 300);
  };

  const handleSelect = (contact) => {
    setSelected(contact);
    setQuery(contact.display_name || contact.name);
    setResults([]);
  };

  const handleConfirm = () => {
    if (!selected) return;
    onConfirm({ name: selected.display_name || selected.name, contact_id: selected.id });
  };

  const label = type === 'visto' ? '🛂 Visto' : '📘 Passaporte';

  return (
    <div style={{ position: 'absolute', bottom: 52, left: 16, right: 16, background: '#fff', border: '1px solid #003A7A', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 20, padding: '14px 16px' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#003A7A', marginBottom: 10 }}>{label} — Selecionar cliente</div>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          value={query}
          onChange={handleQueryChange}
          onKeyDown={e => { if (e.key === 'Escape') onCancel(); }}
          placeholder="Digite o nome ou telefone do cliente..."
          style={{ width: '100%', fontSize: 14, padding: '8px 12px', border: `1px solid ${selected ? '#50CFAD' : '#e2e8f0'}`, borderRadius: 6, outline: 'none', color: '#111b21', boxSizing: 'border-box' }}
        />
        {results.length > 0 && (
          <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 -4px 12px rgba(0,0,0,0.1)', marginBottom: 4, overflow: 'hidden', zIndex: 30 }}>
            {results.map(c => (
              <div key={c.id} onMouseDown={e => { e.preventDefault(); handleSelect(c); }}
                style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
                onMouseEnter={e => e.currentTarget.style.background = '#f0f2f5'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#dfe5e7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, color: '#54656f', flexShrink: 0 }}>
                  {(c.display_name || c.name || '?').slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111b21' }}>{c.display_name || c.name}</div>
                  <div style={{ fontSize: 11, color: '#8696a0' }}>{c.phone}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        {searching && (
          <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#8696a0' }}>buscando...</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
        <button onMouseDown={onCancel} style={{ fontSize: 13, padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#54656f', cursor: 'pointer' }}>
          Cancelar
        </button>
        <button onMouseDown={handleConfirm} disabled={!selected}
          style={{ fontSize: 13, padding: '6px 14px', border: 'none', borderRadius: 6, background: selected ? '#003A7A' : '#ccc', color: '#fff', cursor: selected ? 'pointer' : 'default' }}>
          Criar processo
        </button>
      </div>
    </div>
  );
}


// ── StepPopup ─────────────────────────────────────────────────
function StepPopup({ process, step, stepConfig, sellerId, onClose, onUpdated }) {
  const [date, setDate] = useState(step?.date || '');
  const [fields, setFields] = useState(step?.fields || {});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const hasAttachment = stepConfig?.has_attachment || false;
  const hasDate = stepConfig?.has_date || false;
  const hasFields = stepConfig?.has_fields || null;

  const fieldLabels = {
    login: 'Login', senha: 'Senha', codigo: 'Código de rastreio', para_quem: 'Entregue para',
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await TeamChatApi.uploadStepAttachment(process.id, stepConfig.key, sellerId, file);
      onUpdated();
    } catch (e) { console.error(e); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (hasDate && !date) {
      alert('Informe a data antes de marcar como feito.');
      return;
    }
    if (hasAttachment && !attachmentUrl && !uploading) {
      const confirm = window.confirm('Nenhum anexo enviado. Deseja marcar como feito mesmo assim?');
      if (!confirm) return;
    }
    setSaving(true);
    try {
      const payload = { seller_id: sellerId, done: true };
      if (hasDate && date) payload.date = date;
      if (hasFields) payload.fields = fields;
      await TeamChatApi.updateProcessStep(process.id, stepConfig.key, payload);
      onUpdated();
      onClose();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const attachmentUrl = step?.attachment_url;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 360, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#003A7A' }}>{stepConfig?.label}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#8696a0', lineHeight: 1 }}>×</button>
        </div>

        {hasAttachment && (
          <div style={{ marginBottom: 14 }}>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
              onChange={e => handleUpload(e.target.files[0])} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              style={{ width: '100%', padding: '10px', border: '2px dashed #e2e8f0', borderRadius: 8, background: '#f8f9fa', cursor: 'pointer', fontSize: 13, color: '#54656f' }}>
              {uploading ? 'Enviando...' : attachmentUrl ? '📎 Substituir anexo' : '📎 Anexar imagem ou PDF'}
            </button>
            {attachmentUrl && (
              <a href={attachmentUrl} target="_blank" rel="noopener noreferrer"
                style={{ display: 'block', marginTop: 6, fontSize: 12, color: '#027eb5', textDecoration: 'none' }}>
                ✓ Ver anexo atual
              </a>
            )}
          </div>
        )}

        {hasDate && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, color: '#54656f', fontWeight: 600, display: 'block', marginBottom: 4 }}>Data</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              style={{ width: '100%', fontSize: 14, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none', color: '#111b21', boxSizing: 'border-box' }} />
          </div>
        )}

        {hasFields && hasFields.map(fieldKey => (
          <div key={fieldKey} style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, color: '#54656f', fontWeight: 600, display: 'block', marginBottom: 4 }}>
              {fieldLabels[fieldKey] || fieldKey}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type={fieldKey === 'senha' ? 'password' : 'text'}
                value={fields[fieldKey] || ''}
                onChange={e => setFields(prev => ({ ...prev, [fieldKey]: e.target.value }))}
                style={{ flex: 1, fontSize: 14, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none', color: '#111b21' }}
              />
              {fieldKey === 'codigo' && (
                <button onClick={() => navigator.clipboard.writeText(fields[fieldKey] || '')}
                  title="Copiar"
                  style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8f9fa', cursor: 'pointer', fontSize: 14 }}>
                  📋
                </button>
              )}
            </div>
          </div>
        ))}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button onClick={onClose} style={{ fontSize: 13, padding: '7px 16px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#54656f', cursor: 'pointer' }}>
            Fechar
          </button>
          {(hasDate || hasFields) && (
            <button onClick={handleSave} disabled={saving}
              style={{ fontSize: 13, padding: '7px 16px', border: 'none', borderRadius: 6, background: '#003A7A', color: '#fff', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Salvando...' : 'Marcar como feito'}
            </button>
          )}
          {!hasDate && !hasFields && (
            <button onClick={handleSave} disabled={saving}
              style={{ fontSize: 13, padding: '7px 16px', border: 'none', borderRadius: 6, background: step?.done ? '#8696a0' : '#003A7A', color: '#fff', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Salvando...' : step?.done ? 'Desmarcar' : 'Marcar como feito'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── ProcessCard ───────────────────────────────────────────────
function ProcessCard({ message, sellerId, onReact, onDelete, onReply, onReloadMessages }) {
  const [process, setProcess] = useState(message.process_data || null);
  const [activeStep, setActiveStep] = useState(null);
  const reactions = message.reactions || [];
  const myReaction = reactions.find(r => r.seller_id === sellerId);
  const threadCount = (message.threads || []).length;

  // Sincroniza se a mensagem for atualizada pelo pai
  useEffect(() => {
    if (message.process_data) setProcess(message.process_data);
  }, [message.process_data]);

  const handleStepClick = (stepConfig) => {
    const stepData = process?.steps?.[stepConfig.key] || {};
    setActiveStep({ stepConfig, stepData });
  };

  const typeLabel = message.text?.includes('visto') ? '🛂 Visto' : '📘 Passaporte';
  const contactName = message.text?.split('—')[1]?.trim() || '';

  const stepsConfig = process?.steps_config || [];
  const steps = process?.steps || {};
  const doneCount = Object.values(steps).filter(s => s.done).length;
  const total = stepsConfig.length;
  const allDone = total > 0 && doneCount === total;

  return (
    <div className="tc-msg-card" style={{ borderLeft: `3px solid ${allDone ? '#50CFAD' : '#003A7A'}` }}>
      <div className="tc-msg-actions">
        <button className={`tc-msg-action-btn ${myReaction ? 'confirmed' : ''}`} onClick={() => onReact(message.id)} data-tip={myReaction ? 'Remover' : 'Concluído'}>
          ✓{reactions.length > 0 ? ` ${reactions.length}` : ''}
        </button>
        <button className="tc-msg-action-btn" onClick={() => onReply(message)} data-tip="Responder">
          💬 {threadCount > 0 ? threadCount : ''}
        </button>
        {message.seller_id === sellerId && (
          <button className="tc-msg-action-btn danger" onClick={() => onDelete(message.id)} data-tip="Apagar">🗑</button>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#dfe5e7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, color: '#54656f', flexShrink: 0 }}>
          {initials(message.sellers?.name || '')}
        </div>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#111b21' }}>{message.sellers?.name || 'Equipe'}</span>
          <span style={{ fontSize: 11, color: '#8696a0', marginLeft: 6 }}>{formatTime(message.created_at)}</span>
        </div>
      </div>

      <div style={{ marginLeft: 34 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#003A7A' }}>{typeLabel}</span>
          <span style={{ fontSize: 13, color: '#111b21' }}>{contactName}</span>
          {allDone && <span style={{ fontSize: 11, background: '#50CFAD', color: '#fff', borderRadius: 12, padding: '2px 8px', fontWeight: 600 }}>Concluído</span>}
        </div>

        {!process && <div style={{ fontSize: 12, color: '#8696a0' }}>carregando etapas...</div>}

        {process && stepsConfig.length > 0 && (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {stepsConfig.map(stepConfig => {
                const stepData = process?.steps?.[stepConfig.key] || {};
                const done = stepData.done || false;
                const hasDate = stepConfig.has_date && stepData.date;
                return (
                  <button
                    key={stepConfig.key}
                    onClick={() => handleStepClick(stepConfig)}
                    style={{
                      fontSize: 11,
                      padding: '4px 10px',
                      borderRadius: 20,
                      border: `1px solid ${done ? '#50CFAD' : '#e2e8f0'}`,
                      background: done ? '#50CFAD' : '#f8f9fa',
                      color: done ? '#fff' : '#54656f',
                      cursor: 'pointer',
                      fontWeight: done ? 600 : 400,
                      transition: 'all 0.15s',
                    }}
                  >
                    {done ? '✓ ' : ''}{stepConfig.label}
                    {hasDate && <span style={{ marginLeft: 4, opacity: 0.85 }}>· {new Date(stepData.date).toLocaleDateString('pt-BR')}</span>}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: '#8696a0' }}>
              {doneCount}/{total} etapas concluídas
            </div>
          </>
        )}
      </div>

      {activeStep && (
        <StepPopup
          process={process}
          step={process?.steps?.[activeStep.stepConfig.key] || {}}
          stepConfig={activeStep.stepConfig}
          sellerId={sellerId}
          onClose={() => setActiveStep(null)}
          onUpdated={() => { onReloadMessages(); setActiveStep(null); }}
        />
      )}
    </div>
  );
}

// ── MessageCard ───────────────────────────────────────────────
function MessageCard({ message, sellerId, onReact, onDelete, onReply, onReloadMessages }) {
  if (message.process_id) {
    return <ProcessCard message={message} sellerId={sellerId} onReact={onReact} onDelete={onDelete} onReply={onReply} onReloadMessages={onReloadMessages} />;
  }

  const reactions = message.reactions || [];
  const myReaction = reactions.find(r => r.seller_id === sellerId);
  const threadCount = (message.threads || []).length;

  return (
    <div className="tc-msg-card">
      <div className="tc-msg-actions">
        <button className={`tc-msg-action-btn ${myReaction ? 'confirmed' : ''}`} onClick={() => onReact(message.id)} data-tip={myReaction ? 'Remover' : 'Concluído'}>
          ✓{reactions.length > 0 ? ` ${reactions.length}` : ''}
        </button>
        <button className="tc-msg-action-btn" onClick={() => onReply(message)} data-tip="Responder">
          💬 {threadCount > 0 ? threadCount : ''}
        </button>
        {message.seller_id === sellerId && (
          <button className="tc-msg-action-btn danger" onClick={() => onDelete(message.id)} data-tip="Apagar">🗑</button>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
        <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#dfe5e7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, color: '#54656f', flexShrink: 0 }}>
          {initials(message.sellers?.name || '')}
        </div>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#111b21' }}>{message.sellers?.name || 'Equipe'}</span>
          <span style={{ fontSize: 11, color: '#8696a0', marginLeft: 6 }}>{formatTime(message.created_at)}</span>
        </div>
      </div>

      <p style={{ fontSize: 14, color: '#111b21', margin: '0 0 0 34px', lineHeight: 1.5 }}>{message.text}</p>

      {message.attachment_url && message.attachment_type === 'image' && (
        <img src={message.attachment_url} alt="anexo" onClick={() => window.open(message.attachment_url, '_blank')}
          style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 8, marginTop: 8, marginLeft: 34, cursor: 'pointer', display: 'block', border: '1px solid #e2e8f0' }} />
      )}
      {message.attachment_url && message.attachment_type === 'pdf' && (
        <a href={message.attachment_url} target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, marginLeft: 34, padding: '6px 12px', borderRadius: 8, background: '#f0f2f5', border: '1px solid #e2e8f0', fontSize: 13, color: '#027eb5', textDecoration: 'none' }}>
          📄 Abrir PDF
        </a>
      )}

      <div style={{ marginLeft: 34, marginTop: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
        {myReaction && (
          <span style={{ fontSize: 11, color: '#0F6E56', display: 'flex', alignItems: 'center', gap: 4 }}>
            ✓ {reactions.map(r => r.name.split(' ')[0]).join(', ')}
          </span>
        )}
        {threadCount > 0 && (
          <span onClick={() => onReply(message)} style={{ fontSize: 12, color: '#027eb5', cursor: 'pointer' }}>
            {threadCount} resposta{threadCount > 1 ? 's' : ''} ▾
          </span>
        )}
      </div>
    </div>
  );
}

// ── Componente Principal ──────────────────────────────────────
export default function TeamChatPage() {
  const { profile } = useAuth();
  const isMobile = useIsMobile();
  const sellerId = profile?.seller_id;
  const { byChannel } = useTeamUnread(sellerId);

  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [allSellers, setAllSellers] = useState([]);
  const [showMembers, setShowMembers] = useState(false);
  const [filter, setFilter] = useState('todos');
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [mobileView, setMobileView] = useState('list');
  const [mentionQuery, setMentionQuery] = useState(null);
  const [attachFile, setAttachFile] = useState(null);
  const [threadMessage, setThreadMessage] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [search, setSearch] = useState('');

  // Slash menu
  const [slashMenu, setSlashMenu] = useState(null); // null | 'menu' | 'form'
  const [slashType, setSlashType] = useState(null);  // 'visto' | 'passaporte'

  const realtimeRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!sellerId) return;
    TeamChatApi.getChannels(sellerId)
      .then(data => { setChannels(data); if (!isMobile && data.length > 0) setSelectedChannel(data[0]); })
      .catch(console.error);
  }, [sellerId, isMobile]);

  useEffect(() => {
    if (!selectedChannel) return;
    TeamChatApi.getMembers(selectedChannel.id).then(data => setMembers(data || [])).catch(console.error);
  }, [selectedChannel]);

  useEffect(() => {
    fetch(`${API}/sellers`).then(r => r.json()).then(data => setAllSellers(data || [])).catch(console.error);
  }, []);

  const loadMessages = useCallback(async (channel, f = 'todos') => {
    if (!channel) return;
    setLoading(true);
    try {
      const data = await TeamChatApi.getMessages(channel.id, null);
      setMessages(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadMessages(selectedChannel, filter); }, [selectedChannel, loadMessages]);

  useEffect(() => {
    if (!selectedChannel) return;
    if (realtimeRef.current) realtimeRef.current.unsubscribe();
    realtimeRef.current = subscribeToChannelMessages(selectedChannel.id, (newMsg) => {
      if (newMsg.seller_id !== sellerId) playNotification();
      loadMessages(selectedChannel, filter);
    });
    return () => realtimeRef.current?.unsubscribe();
  }, [selectedChannel, sellerId, filter, loadMessages]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleThreadSent = useCallback(() => {
    loadMessages(selectedChannel, filter);
  }, [selectedChannel, filter, loadMessages]);

  useEffect(() => {
    if (threadMessage && messages.length > 0) {
      const updated = messages.find(m => m.id === threadMessage.id);
      if (updated) setThreadMessage(updated);
    }
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text && !attachFile) return;
    if (!selectedChannel || sending) return;
    setSending(true);
    try {
      if (attachFile) {
        const form = new FormData();
        form.append('file', attachFile);
        form.append('seller_id', sellerId);
        if (text) form.append('text', text);
        await fetch(`${API}/team/channels/${selectedChannel.id}/upload`, { method: 'POST', body: form });
        setAttachFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        await TeamChatApi.sendMessage(selectedChannel.id, sellerId, text);
      }
      setInput('');
      playNotification();
      await loadMessages(selectedChannel, filter);
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  const handleToggleReaction = async (messageId) => {
    try {
      await fetch(`${API}/team/messages/${messageId}/reactions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ seller_id: sellerId }),
      });
      await loadMessages(selectedChannel, filter);
    } catch (e) { console.error(e); }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await fetch(`${API}/team/messages/${messageId}?seller_id=${sellerId}`, { method: 'DELETE' });
      if (threadMessage?.id === messageId) setThreadMessage(null);
      await loadMessages(selectedChannel, filter);
    } catch (e) { console.error(e); }
  };

  const handleAddMember = async (s) => {
    try {
      await TeamChatApi.addMember(selectedChannel.id, s.id);
      const updated = await TeamChatApi.getMembers(selectedChannel.id);
      setMembers(updated || []);
    } catch (e) { console.error(e); }
  };

  const handleCreateChannel = async () => {
    const name = newChannelName.trim();
    if (!name || !sellerId) return;
    try {
      const channel = await TeamChatApi.createChannel(sellerId, name);
      setChannels(prev => [...prev, channel]);
      setNewChannelName(''); setShowNewChannel(false); setSelectedChannel(channel);
    } catch (e) { alert(e.message); }
  };

  const handleSelectChannel = (ch) => {
    setSelectedChannel(ch);
    setFilter('todos');
    setThreadMessage(null);
    if (isMobile) setMobileView('chat');
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInput(val);

    // Slash commands
    if (val === '/') {
      setSlashMenu('menu');
      setMentionQuery(null);
      return;
    }
    if (val.toLowerCase() === '/visto') {
      setSlashMenu('form');
      setSlashType('visto');
      setInput('');
      return;
    }
    if (val.toLowerCase() === '/passaporte') {
      setSlashMenu('form');
      setSlashType('passaporte');
      setInput('');
      return;
    }
    if (!val.startsWith('/')) setSlashMenu(null);

    // Mentions
    const atIdx = val.lastIndexOf('@');
    if (atIdx !== -1 && !val.slice(atIdx + 1).includes(' ')) {
      setMentionQuery(val.slice(atIdx + 1));
    } else {
      setMentionQuery(null);
    }
  };

  const handleSlashSelect = (type) => {
    setSlashType(type);
    setSlashMenu('form');
    setInput('');
  };

  const handleCreateProcess = async ({ name: contactName, contact_id: contactId }) => {
    if (!contactName || !slashType || !selectedChannel) return;
    setSending(true);
    try {
      await TeamChatApi.createProcess(selectedChannel.id, sellerId, slashType, contactName, contactId);
      setSlashMenu(null);
      setSlashType(null);
      playNotification();
      await loadMessages(selectedChannel, filter);
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  const handleCancelSlash = () => {
    setSlashMenu(null);
    setSlashType(null);
    setInput('');
  };

  const handleMentionSelect = (member) => {
    const atIdx = input.lastIndexOf('@');
    setInput(input.slice(0, atIdx) + '@' + member.name.split(' ')[0] + ' ');
    setMentionQuery(null);
  };

  const filteredMessages = (() => {
    let msgs = messages;
    if (search.trim()) msgs = msgs.filter(m => m.text.toLowerCase().includes(search.toLowerCase()));
    if (filter === 'confirmados') msgs = msgs.filter(m => (m.reactions || []).length > 0);
    if (filter === 'nao-confirmados') msgs = msgs.filter(m => (m.reactions || []).length === 0);
    return msgs;
  })();

  const messagesWithDates = (() => {
    const result = [];
    let lastDate = null;
    for (const msg of filteredMessages) {
      const label = formatDateLabel(msg.created_at);
      if (label !== lastDate) {
        result.push({ type: 'date', label, key: `date-${label}` });
        lastDate = label;
      }
      result.push({ type: 'msg', msg });
    }
    return result;
  })();

  // ── Sidebar ───────────────────────────────────
  const renderSidebar = () => (
    <div className="team-chat__list">
      <div className="team-chat__list-header">
        <h3>Equipe</h3>
        <button onClick={() => setShowNewChannel(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#54656f', display: 'flex', alignItems: 'center' }}>
          <Plus size={18} />
        </button>
      </div>

      {showNewChannel && (
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 6 }}>
          <input autoFocus value={newChannelName} onChange={e => setNewChannelName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreateChannel(); if (e.key === 'Escape') { setShowNewChannel(false); setNewChannelName(''); } }}
            placeholder="nome-do-canal"
            style={{ flex: 1, fontSize: 13, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#111b21', outline: 'none' }} />
          <button onClick={() => { setShowNewChannel(false); setNewChannelName(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8696a0' }}>
            <X size={14} />
          </button>
        </div>
      )}

      <div className="team-chat__section-label">canais</div>

      {channels.map(ch => {
        const isActive = selectedChannel?.id === ch.id;
        const chCount = byChannel[ch.id] || 0;
        return (
          <div key={ch.id} className={`team-chat__item ${isActive ? 'active' : ''}`} onClick={() => handleSelectChannel(ch)}>
            <div className="team-chat__item-icon" style={{ borderRadius: 6, fontSize: 12 }}>#</div>
            <div className="team-chat__item-info">
              <span className="team-chat__item-name" style={{ fontWeight: chCount > 0 && !isActive ? 600 : 400 }}>{ch.name}</span>
            </div>
            {chCount > 0 && !isActive && (
              <span className="badge" style={{ background: '#E24B4A', fontSize: 10, minWidth: 18, height: 18 }}>{chCount}</span>
            )}
          </div>
        );
      })}
    </div>
  );

  // ── Área de chat ──────────────────────────────
  const renderChat = () => (
    <div className="team-chat__chat">
      <div className="team-chat__chat-header">
        {isMobile && (
          <button className="team-chat__back-btn" onClick={() => setMobileView('list')} style={{ color: '#54656f' }}>
            <ArrowLeft size={20} />
          </button>
        )}
        <h3># {selectedChannel?.name}</h3>

        <div className="tc-filter-bar" style={{ marginLeft: 'auto' }}>
          {['todos', 'confirmados', 'nao-confirmados'].map(f => (
            <button key={f} className={`tc-filter-btn ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
              {f === 'todos' ? 'todos' : f === 'confirmados' ? '✓ confirmados' : 'pendentes'}
            </button>
          ))}
          <button onClick={() => setShowMembers(v => !v)} title="Membros"
            style={{ background: showMembers ? '#f0f2f5' : 'none', border: 'none', cursor: 'pointer', color: '#54656f', display: 'flex', alignItems: 'center', padding: '4px 6px', borderRadius: 6 }}>
            <Users size={15} />
            <span style={{ fontSize: 11, marginLeft: 4 }}>{members.length}</span>
          </button>
          <button onClick={() => { setShowSearch(v => !v); setSearch(''); }} title="Buscar"
            style={{ background: showSearch ? '#f0f2f5' : 'none', border: 'none', cursor: 'pointer', color: '#54656f', display: 'flex', alignItems: 'center', padding: '4px 6px', borderRadius: 6, fontSize: 14 }}>
            &#128269;
          </button>
        </div>
      </div>

      {showMembers && (() => {
        const memberIds = new Set(members.map(m => m.id));
        const nonMembers = allSellers.filter(s => !memberIds.has(s.id));
        return (
          <div style={{ padding: '10px 16px', background: '#f0f2f5', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {members.map(m => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#dfe5e7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 600, color: '#54656f' }}>
                  {initials(m.name)}
                </div>
                <span style={{ fontSize: 12, color: '#54656f' }}>{m.name}</span>
              </div>
            ))}
            {nonMembers.map(s => (
              <button key={s.id} onClick={() => handleAddMember(s)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 12, border: '1px dashed #8696a0', background: 'transparent', color: '#54656f', cursor: 'pointer' }}>
                + {s.name.split(' ')[0]}
              </button>
            ))}
          </div>
        );
      })()}

      {showSearch && (
        <div style={{ padding: '8px 16px', background: '#f0f2f5', borderBottom: '1px solid #e2e8f0' }}>
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Escape' && setShowSearch(false)}
            placeholder="Buscar no canal..."
            style={{ width: '100%', fontSize: 13, padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 20, background: '#fff', color: '#111b21', outline: 'none' }} />
          {search && <div style={{ fontSize: 11, color: '#8696a0', marginTop: 4 }}>{filteredMessages.length} resultado{filteredMessages.length !== 1 ? 's' : ''}</div>}
        </div>
      )}

      <div className="team-chat__messages">
        {loading && <div className="team-chat__loading">carregando...</div>}
        {!loading && filteredMessages.length === 0 && (
          <div className="team-chat__empty">Nenhuma mensagem.</div>
        )}
        {!loading && messagesWithDates.map(item => {
          if (item.type === 'date') {
            return (
              <div key={item.key} className="tc-date-separator">
                <span>{item.label}</span>
              </div>
            );
          }
          return (
            <MessageCard
              key={item.msg.id}
              message={item.msg}
              sellerId={sellerId}
              onReact={handleToggleReaction}
              onDelete={handleDeleteMessage}
              onReply={setThreadMessage}
              onReloadMessages={() => loadMessages(selectedChannel, filter)}
            />
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <div className="team-chat__input-area" style={{ position: 'relative' }}>
        {slashMenu === 'menu' && (
          <SlashMenu onSelect={handleSlashSelect} />
        )}
        {slashMenu === 'form' && slashType && (
          <ProcessForm type={slashType} onConfirm={handleCreateProcess} onCancel={handleCancelSlash} />
        )}
        {slashMenu === null && mentionQuery !== null && (
          <MentionDropdown members={members} query={mentionQuery} onSelect={handleMentionSelect} />
        )}
        {attachFile && (
          <div style={{ position: 'absolute', bottom: 56, left: 16, right: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#111b21' }}>
            <span>{attachFile.type === 'application/pdf' ? '📄' : '🖼'}</span>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{attachFile.name}</span>
            <button onClick={() => { setAttachFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8696a0', fontSize: 16 }}>×</button>
          </div>
        )}
        <input ref={fileInputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => setAttachFile(e.target.files[0] || null)} />
        <button onClick={() => fileInputRef.current?.click()} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#54656f', fontSize: 18, padding: '0 4px', flexShrink: 0 }} title="Anexar">📎</button>
        <input className="team-chat__input" value={input} onChange={handleInputChange}
          onKeyDown={e => { if (e.key === 'Enter' && slashMenu === null) handleSend(); if (e.key === 'Escape') handleCancelSlash(); }}
          placeholder={`Mensagem em #${selectedChannel?.name}... (/ para processos)`} />
        <button className="team-chat__send-btn" onClick={handleSend} disabled={(!input.trim() && !attachFile) || sending || slashMenu !== null}>›</button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className="team-chat team-chat--mobile">
        {mobileView === 'list' ? (
          <div className="team-chat__mobile-list">
            <div className="team-chat__mobile-list-header"><h2>Equipe</h2></div>
            {channels.map(ch => (
              <div key={ch.id} className="team-chat__mobile-item" onClick={() => handleSelectChannel(ch)}>
                <div className="team-chat__mobile-item-icon" style={{ borderRadius: 8, fontSize: 16 }}>#</div>
                <div className="team-chat__mobile-item-info">
                  <span className="team-chat__mobile-item-name">{ch.name}</span>
                  <span className="team-chat__mobile-item-sub">{ch.description || 'Canal'}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="team-chat__mobile-chat">{renderChat()}</div>
        )}
      </div>
    );
  }

  return (
    <div className="team-chat">
      {renderSidebar()}
      {selectedChannel ? (
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {renderChat()}
          {threadMessage && (
            <ThreadPanel
              message={threadMessage}
              sellerId={sellerId}
              onClose={() => setThreadMessage(null)}
              onThreadSent={handleThreadSent}
            />
          )}
        </div>
      ) : (
        <div className="team-chat__chat" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: '#8696a0', fontSize: 14 }}>Selecione um canal</p>
        </div>
      )}
    </div>
  );
}
